import { type IntlShape } from 'react-intl-next';

import { type PeriodRangeEdge } from '../../types';

import { Layout } from './constants';
import { messages } from './messages';
import { type ShiftHeight } from './types';

const findLayout = (width: number, size: ShiftHeight) => {
	const slim = size === 'small' || size === 'medium';

	if (width < 16) {
		return slim ? Layout.NoneSlimView : Layout.NoneView;
	} else if (width < 36) {
		return slim ? Layout.MiniSlimView : Layout.MiniView;
	} else if (width < 52) {
		return slim ? Layout.CompactSlimView : Layout.CompactView;
	} else if (width < 100) {
		return slim ? Layout.SparseSlimView : Layout.SparseView;
	} else {
		return slim ? Layout.FullSlimView : Layout.FullView;
	}
};

const formatName = (name: string = '', layout: Layout, isNoOne: boolean, isDeleted: boolean) => {
	if (isNoOne) {
		switch (layout) {
			case Layout.CompactView:
			case Layout.CompactSlimView:
			case Layout.SparseView:
			case Layout.SparseSlimView:
			case Layout.MiniView:
			case Layout.MiniSlimView:
				return '-';
			case Layout.NoneView:
			case Layout.NoneSlimView:
				return '';
			case Layout.FullView:
			case Layout.FullSlimView:
			default:
				return name;
		}
	}

	if (isDeleted) {
		switch (layout) {
			case Layout.CompactView:
			case Layout.CompactSlimView:
			case Layout.SparseView:
			case Layout.SparseSlimView:
				return name
					.split(' ')
					.map((n) => n[0])
					.join('');
			case Layout.MiniView:
			case Layout.MiniSlimView:
			case Layout.NoneView:
			case Layout.NoneSlimView:
				return '';
			case Layout.FullView:
			case Layout.FullSlimView:
			default:
				return name;
		}
	}

	switch (layout) {
		case Layout.CompactView:
		case Layout.CompactSlimView:
		case Layout.SparseView:
		case Layout.SparseSlimView:
			return name
				.split(' ')
				.map((n) => n[0])
				.join('');
		case Layout.MiniView:
		case Layout.MiniSlimView:
			return name[0];
		case Layout.NoneView:
		case Layout.NoneSlimView:
			return '';
		case Layout.FullView:
		case Layout.FullSlimView:
		default:
			return name;
	}
};

const formatTimeRange = (intlApi: IntlShape, time: PeriodRangeEdge) => {
	const startDate = new Date(time.startEpoch + time.startOffset);
	const endDate = new Date(time.endEpoch + time.endOffset);

	return intlApi.formatMessage(messages.timeRange, {
		startHourMinute: time.isTrueStartTime
			? intlApi.formatDate(startDate, {
					hour: 'numeric',
					minute: startDate.getMinutes() === 0 ? undefined : 'numeric',
					timeZone: 'UTC',
				})
			: intlApi.formatMessage(messages.startTimeUnknown),
		endHourMinute: time.isTrueEndTime
			? intlApi.formatDate(endDate, {
					hour: 'numeric',
					minute: endDate.getMinutes() === 0 ? undefined : 'numeric',
					timeZone: 'UTC',
				})
			: intlApi.formatMessage(messages.endTimeUnknown),
	});
};

export { findLayout, formatTimeRange, formatName };
